import React, { useEffect, useContext, useState, useCallback } from 'react';
import {
	Box,
	Drawer,
	InputLabel,
	IconButton,
	Button,
	TextField,
	Grid,
	ListItem,
	ListItemIcon,
	ListItemText,
	InputAdornment,
	Tabs,
	Tab,
	List,
	Switch,
	Divider,
	Tooltip
} from '@material-ui/core';
import PropTypes from 'prop-types';
import Autocomplete from '@material-ui/lab/Autocomplete';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ErrorIcon from '@material-ui/icons/Error';
import SearchIcon from '@material-ui/icons/Search';
import shareReportStyles from './ShareReport.styles';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import CloseIcon from '@material-ui/icons/Close';
import {
	getUserList,
	getMerchantUserList,
	getDVUserList,
	setInvitedUsers,
	setUsersWithReportAccess,
	setUsersWithReportAccessSuccess,
	resetUsersWithReportAccess
} from 'components/Dashboard/Dashboard.actions';
import { useDispatch, useSelector } from 'react-redux';
import { UserPortalContext } from 'context/appcontext';
import SharedWithList from './SharedWithList';
import DialogBox from '../Shared/DialogBox';
import {
	SHARE_REPORT_TITLE,
	SHARE_SEARCH_PLACEHOLDER,
	INVITES,
	MESSAGE,
	OPTIONAL,
	SEND,
	CANCEL,
	ALREADY_INVITED_ERROR,
	DISCARD,
	DISCARD_WARNING,
	REPORT_SHARE_MSG,
	MERCHANT_TAG,
	WMT_OTHER_TAG,
	NO_MATCHING_RESULT,
	TRY_SOMETHING_ELSE,
	USER_LIST_API_FAILURE,
	USER_LIST_API_FAILURE_SUPPORT,
	DATA_VENTURES,
	SHARING_SETTINGS_SAVED,
	SUPPLIER_TAG,
	CATMAN_TAG,
	INCLUDE_DATA,
	DISABLE_INCLUDE_DATA_TOOLTIP,
	INCLUDE_DATA_HELP,
	VSC_TO_CATMAN_ERROR,
	CATMAN_CUSTOM_DATASET_IDS,
	CANNED_REPORT_LIST
} from 'constants/constant';
import { contains, debounce, deepClone } from 'helper/helper';
import { trackEventInfo } from 'helper/analytics';
import { EVENTS, PAGE_ID, ACTION_ON } from 'constants/tracker';
import TabPanel from 'components/Template/left-panel/TabPanel';
import CircularProgress from '@material-ui/core/CircularProgress';
import RBSnackbar from 'components/Shared/RBSnackbar';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { isCustomDataset } from 'helper/utils';

const ShareReport = (props) => {
	const { show, setShow, handleBack, data, syncWithBE, setSharedWith } = props;
	const reportId = data?.reportId;
	const portalUserContext = useContext(UserPortalContext);
	const { userDetails } = portalUserContext;
	const { email, companyName, userPersona } = userDetails?.result;

	const classes = shareReportStyles();
	const dispatch = useDispatch();

	const userList = useSelector((state) => state.dashboardReducer.userList);
	const catmanUserList = useSelector((state) => state.dashboardReducer.catmanUserList);
	const merchantUserList = useSelector((state) => state.dashboardReducer.merchantUserList);
	const dvUserList = useSelector((state) => state.dashboardReducer.dvUserList);
	const invitedUserList = useSelector((state) => state.dashboardReducer.invitedUsers);
	const usersToRemoveAccess = useSelector((state) => state.dashboardReducer.usersToRemoveAccess);
	const usersWithReportAccess = useSelector(
		(state) => state.dashboardReducer.usersWithReportAccess
	);
	const userListLoading = useSelector((state) => state.dashboardReducer.userListLoading);
	const merchUserListLoading = useSelector((state) => state.dashboardReducer.merchUserListLoading);
	const dvUserListLoading = useSelector((state) => state.dashboardReducer.dvUserListLoading);
	const userListErrorCode = useSelector((state) => state.dashboardReducer.userListErrorCode);
	const merchantUserListErrorCode = useSelector(
		(state) => state.dashboardReducer.merchantUserListErrorCode
	);
	const dvUserListErrorCode = useSelector((state) => state.dashboardReducer.dvUserListErrorCode);
	const selectedTemplate = useSelector((state) => state.templateReducer.selectedTemplate);
	const customDatasetDetails = useSelector((state) => state.templateReducer.customDatasetDetails);

	const [shouldDisable, setShouldDisable] = useState(true);
	const [count, setCount] = useState(0);
	const [showError, setShowError] = useState(false);
	const [message, setMessage] = useState('');
	const [showDiscardMsg, setShowDiscardMsg] = useState(false);
	const [valueState, setValueState] = useState('');
	const [showShareMsg, setShowShareMsg] = useState(true);
	const [tabValue, setTabValue] = useState(0);
	const [showTabs, setShowTabs] = useState(false);
	const [openSnackbar, setSnackbar] = useState({ open: false, msg: '' });
	const [includeData, setIncludeData] = useState(false);

	const templateId = selectedTemplate ? Number(selectedTemplate) : Number(data?.templateId);
	// If it is a custom dataset report, then switch should be always on
	let shouldIncludeData =
		CATMAN_CUSTOM_DATASET_IDS.includes(Number(data?.templateId)) ||
		data?.templateId === customDatasetDetails?.templateId ||
		isCustomDataset(selectedTemplate ?? data?.templateId);

	let TABS = [
		{
			label: SUPPLIER_TAG,
			list: userList,
			errorCode: userListErrorCode,
			userType: SUPPLIER_TAG
		},
		{
			label: CATMAN_TAG,
			list: catmanUserList,
			errorCode: userListErrorCode,
			userType: CATMAN_TAG
		},
		{
			label: WMT_OTHER_TAG,
			list: dvUserList,
			errorCode: dvUserListErrorCode,
			userType: DATA_VENTURES
		},
		{
			label: MERCHANT_TAG,
			list: merchantUserList,
			errorCode: merchantUserListErrorCode,
			userType: MERCHANT_TAG
		}
	];

	const [tabs, setTabs] = useState(TABS);

	const toggleDrawer = () => {
		if (invitedUserList.length > 0 || usersToRemoveAccess.length > 0) {
			setShowDiscardMsg(true);
		} else {
			onClose();
		}
	};

	const onClose = () => {
		setShow(false);
		setShowDiscardMsg(false);
		dispatch(resetUsersWithReportAccess());
	};

	const handleUserSelection = (event, value, reason = '') => {
		if (reason !== 'clear') {
			if (value !== null) {
				let sharedWith = [];
				value.userType = tabs[tabValue].userType;
				if (Array.isArray(usersWithReportAccess)) {
					sharedWith = usersWithReportAccess;
				} else {
					sharedWith.push(usersWithReportAccess);
				}
				if (sharedWith.filter((user) => user.email === value.email).length > 0) {
					setShowError(true);
					return;
				}
				if (
					!contains(
						invitedUserList.map((user) => user.email),
						value.email
					)
				) {
					setShowError(false);
					invitedUserList.push(value);
					setCount(invitedUserList.length);
					setValueState('');
					setShouldDisable(false);
					setShowTabs(false);
					dispatch(setInvitedUsers(invitedUserList));
				} else {
					setShowError(true);
				}
			}
		} else {
			setShowError(false);
			setShowTabs(false);
		}
	};

	const handleMessageChange = (event) => {
		const value = event.target.value;
		setMessage(value);
	};

	const hideShareMsg = () => {
		setShowShareMsg(false);
	};

	const grantAccess = () => {
		let isDataIncluded = shouldDisableIncludeData() ? false : includeData;
		if (shouldIncludeData) {
			isDataIncluded = true;
		}
		const grantAccessRequest = invitedUserList.map((user) => ({
			firstName: user.firstName,
			lastName: user.lastName,
			email: user.email,
			userType: user.userType,
			message: message,
			isDataIncluded
		}));
		if (syncWithBE) {
			dispatch(
				setUsersWithReportAccess({
					reportId,
					email,
					companyName,
					requestBody: grantAccessRequest
				})
			);
			// Analytics
			trackEventInfo({
				type: EVENTS.CLICK,
				actionOn: ACTION_ON.SHARE_REPORT_BUTTON,
				location: PAGE_ID.DASHBOARD
			});
			if (isCustomDataset(selectedTemplate)) {
				trackEventInfo({
					type: EVENTS.CLICK,
					actionOn: ACTION_ON.CUSTOM_DATASET_SHARE_REPORT,
					location: PAGE_ID.REPORT_SETTINGS
				});
			}
		} else {
			setSharedWith(grantAccessRequest);
		}
		setSnackbar({ open: true, msg: SHARING_SETTINGS_SAVED, type: 'success' });
		onClose();
	};

	const getUserListDebounced = (search) => {
		if (search) {
			dispatch(getUserList({ requestBody: email, companyName, search }));
			dispatch(getMerchantUserList({ requestBody: email, companyName, search }));
			dispatch(getDVUserList({ requestBody: email, companyName, search }));
		}
	};
	const getList = useCallback(debounce(getUserListDebounced, 1000), []);

	useEffect(() => {
		if (show) {
			setValueState('');
			setShowTabs(false);
			setShowShareMsg(true);
			if (data) {
				dispatch(setUsersWithReportAccessSuccess(data.sharedWith));
			}
			setShouldDisable(invitedUserList.length === 0);
			setShowError(false);
		}
	}, [show]);

	useEffect(() => {
		if (userPersona === 'CATMAN') {
			TABS.splice(0, 1);
		} else if (userPersona === 'SUPPLIER') {
			TABS.splice(1, 1);
		}
		setTabs(TABS);
	}, [userPersona]);

	useEffect(() => {
		setShouldDisable(count === 0);
		if (setSharedWith) {
			setSharedWith(invitedUserList);
		}
	}, [count]);

	/**
	 * We need to change the selected tab in the case of the user list for the tab has no value but other tab has
	 */
	useEffect(() => {
		/**
		 * Vendor scorecard reports cannot be shared with CatMan hence to show the appropriate error message we are hardcoding errorcode of 999 to catman tab
		 */
		let updatedTabs = deepClone(tabs);
		updatedTabs.forEach((tab, idx) => {
			switch (tab.label) {
				case SUPPLIER_TAG:
					tab.list = userList;
					tab.errorCode = userListErrorCode;
					break;
				case CATMAN_TAG:
					tab.list = CANNED_REPORT_LIST.includes(templateId) ? [] : catmanUserList;
					tab.errorCode = CANNED_REPORT_LIST.includes(templateId) ? 999 : userListErrorCode;
					break;
				case MERCHANT_TAG:
					tab.list = merchantUserList;
					tab.errorCode = merchantUserListErrorCode;
					break;
				case WMT_OTHER_TAG:
					tab.list = dvUserList;
					tab.errorCode = dvUserListErrorCode;
					break;
			}
			if (tab.list?.length > 0) {
				setTabValue(idx);
			}
		});
		setTabs(updatedTabs);
	}, [
		merchantUserList,
		merchantUserListErrorCode,
		dvUserList,
		dvUserListErrorCode,
		userList,
		userListErrorCode,
		catmanUserList
	]);

	const cbOnShareDialogOkay = () => {
		onClose();
	};

	const cbOnShareDialogCancel = () => {
		setShowDiscardMsg(false);
	};

	const ShowShareReportDialog = () => {
		let dialogProps = {
			title: '',
			bodyContent: DISCARD_WARNING,
			secondaryBtnText: DISCARD,
			primaryBtnText: CANCEL,
			onSecondaryClick: cbOnShareDialogOkay,
			onPrimaryClick: cbOnShareDialogCancel
		};

		dialogProps.hideTitle = true;

		return <DialogBox open={showDiscardMsg} {...dialogProps} />;
	};

	const getTabLabel = (index) => {
		/**
		 * For VSC type of report, we don't need to show list count for catman tab
		 */

		const shouldDisplayCount = !(
			CANNED_REPORT_LIST.includes(templateId) && tabs[index].label === CATMAN_TAG
		);
		return (
			<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
				<div>{tabs[index].label}</div>
				{shouldDisplayCount && (
					<div className="userCount">
						<span>{tabs[index].list.length}</span>
					</div>
				)}
			</div>
		);
	};
	const handleChange = (event, newValue) => {
		setTabValue(newValue);
	};

	const getTabs = () => {
		return tabs.map((item, index) => (
			<Tab
				label={getTabLabel(index)}
				key={item.label}
				classes={{ root: classes.tabRoot }}
				id={`tab-${item.label.replace(' ', '-')}`}
			/>
		));
	};

	const shouldDisableIncludeData = () => {
		/**
		 * User persona | data can be included if report is shared with
		 * Merchant		| Merchant or DV
		 * Catman		| Merchant or DV
		 * Supplier		| Supplier or Mechant or DV
		 */

		if (userPersona === 'MERCHANT') {
			const noDataAllowedTypes = [SUPPLIER_TAG, CATMAN_TAG];
			return invitedUserList.some((user) => noDataAllowedTypes.includes(user.userType));
		} else {
			const noDataAllowedTypes = [CATMAN_TAG];
			return invitedUserList.some((user) => noDataAllowedTypes.includes(user.userType));
		}
	};

	const getSwitch = (isDisabled) => {
		let disableSwitch = isDisabled;
		let otherProps = {};
		if (shouldIncludeData) {
			disableSwitch = true;
			otherProps = { defaultChecked: true };
		}

		return (
			<Switch
				color="primary"
				name="include-data"
				id="include-data"
				value={includeData}
				{...otherProps}
				onChange={(e) => {
					if (shouldIncludeData) {
						return;
					}
					setIncludeData(e.currentTarget.checked);
				}}
				disabled={disableSwitch}
				inputProps={{ 'aria-label': 'primary checkbox' }}
			/>
		);
	};

	const getErrorMessage = (errorCode) => {
		/** Currently Vendor Scorecard report cannot be shared with Category Advisors
		 * this can be determined by hardcoded errorcode 999 for catman tab
		 * */
		if (errorCode === 0) {
			return (
				<>
					<InputLabel className={classes.userNameLabel}>{NO_MATCHING_RESULT}</InputLabel>
					<InputLabel className={classes.userLoginIdLabel}>{TRY_SOMETHING_ELSE}</InputLabel>
				</>
			);
		} else if (errorCode === 999) {
			return <InputLabel className={classes.userNameLabel}>{VSC_TO_CATMAN_ERROR}</InputLabel>;
		}
		return (
			<>
				<InputLabel className={classes.userNameLabel}>{USER_LIST_API_FAILURE}</InputLabel>
				<InputLabel className={classes.userLoginIdLabel}>
					{USER_LIST_API_FAILURE_SUPPORT}
				</InputLabel>
			</>
		);
	};

	return (
		<>
			<Box>
				<Drawer
					variant="temporary"
					anchor="right"
					open={show}
					id={'share-report-drawer'}
					onClose={toggleDrawer}>
					<Box className={classes.drawerContainer}>
						<Box className={`${classes.drawerContent} ${classes.drawerContentWithoutPadding}`}>
							<Box id="share-report" className={classes.drawer}>
								<Box id="insghts-detail" className={classes.smallScreen}>
									<IconButton
										id="toolbar-back"
										size="medium"
										onClick={() => handleBack()}
										aria-label="Back"
										className={classes.backIcon}>
										<ChevronLeftIcon fontSize="large" />
									</IconButton>
									<InputLabel className={classes.drawerTitle}>{SHARE_REPORT_TITLE}</InputLabel>
								</Box>
							</Box>
						</Box>
						<Box
							className={
								showError
									? `${classes.drawerContent} ${classes.drawerContentWithoutPadding}`
									: `${classes.drawerContent}`
							}>
							<Box id="search-user-list">
								<Box spacing={2}>
									<Autocomplete
										id="user-list-search"
										inputValue={valueState}
										className={classes.autoComplete}
										options={[]}
										filterOptions={(options) => options}
										freeSolo={true}
										getOptionLabel={(option) => {
											return `${option.firstName} ${option.lastName}`;
										}}
										renderOption={(props) => (
											<Grid {...props} container direction="row" alignItems="center">
												<Grid item xs={9}>
													<InputLabel className={classes.userNameLabel}>
														{`${props.firstName} ${props.lastName}`}
													</InputLabel>
													<InputLabel className={classes.userLoginIdLabel}>
														{props.email}
													</InputLabel>
												</Grid>
											</Grid>
										)}
										renderInput={(params) => (
											<TextField
												{...params}
												label="Search"
												placeholder={SHARE_SEARCH_PLACEHOLDER}
												variant="outlined"
												InputProps={{
													...params.InputProps,
													startAdornment: (
														<InputAdornment position="start">
															<SearchIcon />
														</InputAdornment>
													)
												}}
											/>
										)}
										onChange={handleUserSelection}
										onInputChange={(e) => {
											setValueState(e?.currentTarget?.value ? e?.currentTarget?.value : '');
											setShowTabs(true);
											getList(e?.currentTarget?.value ? e?.currentTarget?.value : '');
										}}
									/>
								</Box>
							</Box>
						</Box>
						<Box
							className={showError ? classes.drawerContent : classes.hideBox}
							style={{ padding: '0px' }}>
							<ListItem>
								<ListItemIcon className={classes.errorIcon}>
									<InputLabel error>
										<ErrorIcon />
									</InputLabel>
								</ListItemIcon>
								<ListItemText>
									<InputLabel error className={classes.errorContainer}>
										{ALREADY_INVITED_ERROR}
									</InputLabel>
								</ListItemText>
							</ListItem>
						</Box>
						{showTabs && (
							<Box className={`${classes.drawerContent}`}>
								<Box
									className={classes.tabContainer}
									sx={{ borderBottom: 1, borderColor: 'divider' }}>
									{userListLoading || merchUserListLoading || dvUserListLoading ? (
										<CircularProgress size="24px" />
									) : (
										<>
											<Tabs
												value={tabValue}
												classes={{
													root: classes.tabsRoot,
													indicator: classes.tabsIndicator
												}}
												variant={'fullWidth'}
												aria-label="user list tabs"
												onChange={handleChange}
												indicatorColor="primary">
												{getTabs()}
											</Tabs>

											<TabPanel
												value={tabValue}
												index={tabValue}
												padding={tabs[tabValue].list.length > 0 ? 0 : 2}
												style={{ maxHeight: '158px', overflow: 'auto' }}>
												{tabs[tabValue].list.length > 0 && tabs[tabValue].errorCode === 0 ? (
													<List disablePadding>
														{tabs[tabValue].list.map((option, index) => (
															<>
																<ListItem
																	button
																	key={option.loginId}
																	onClick={(event) => handleUserSelection(event, option)}
																	divider={index !== tabs[tabValue].list.length - 1}
																	className={
																		index !== tabs[tabValue].list.length - 1
																			? classes.userListItem
																			: `${classes.userListItem} ${classes.userListItemLast}`
																	}>
																	<Grid {...props} container direction="row" alignItems="center">
																		<Grid item xs={9}>
																			<InputLabel className={classes.userNameLabel}>
																				{`${option.firstName} ${option.lastName}`}
																			</InputLabel>
																			<InputLabel className={classes.userLoginIdLabel}>
																				{option.email}
																			</InputLabel>
																		</Grid>
																	</Grid>
																</ListItem>
															</>
														))}
													</List>
												) : (
													<Grid
														{...props}
														container
														direction="row"
														alignItems="center"
														justifyContent="center"
														style={{ textAlign: 'center' }}>
														<Grid item xs={9}>
															{getErrorMessage(tabs[tabValue].errorCode)}
														</Grid>
													</Grid>
												)}
											</TabPanel>
										</>
									)}
								</Box>
							</Box>
						)}
						{!showTabs && (
							<Box className={classes.drawerContent}>
								<TextField
									id="message"
									label={`${MESSAGE} (${OPTIONAL})`}
									variant="outlined"
									fullWidth
									multiline
									InputProps={{
										classes: {
											input: classes.text
										}
									}}
									inputProps={{
										maxLength: 500
									}}
									InputLabelProps={{
										classes: {
											root: classes.text
										}
									}}
									onChange={handleMessageChange}
								/>
							</Box>
						)}
						<Box id="shared-with" className={classes.deleteReport}>
							{INVITES}
						</Box>
						{showShareMsg && (
							<Box style={{ padding: '4px', display: 'flex' }}>
								<Box
									id="share-msg-icon"
									className={classes.deleteReport}
									style={{ padding: '8px' }}>
									<ErrorOutlineIcon />
								</Box>
								<Box
									id="share-msg"
									className={classes.deleteReport}
									style={{ padding: 0, paddingTop: '4px', fontSize: '12px' }}>
									{REPORT_SHARE_MSG}
								</Box>
								<Box
									id="share-msg-close"
									className={classes.deleteReport}
									style={{ padding: '8px' }}>
									<CloseIcon
										role="button"
										tabIndex="0"
										aria-label="Close"
										style={{ height: 20 }}
										onClick={hideShareMsg}
									/>
								</Box>
							</Box>
						)}
						<Box className={`${classes.drawerContent} ${classes.inviteesDrawerContent}`}>
							<SharedWithList
								open={false}
								key={invitedUserList}
								userList={invitedUserList}
								data={data}
								isRemovable={true}
								hasModifyAccessMenu={false}
								setCount={setCount}
							/>
						</Box>
					</Box>

					<Divider style={{ position: 'fixed', bottom: '160x', width: '360px' }} />
					<Box className={classes.includeDataContainer}>
						<span style={{ marginLeft: '28px' }}>{INCLUDE_DATA}</span>
						{shouldDisableIncludeData() ? (
							<Tooltip
								title={DISABLE_INCLUDE_DATA_TOOLTIP}
								style={{ cursor: 'not-allowed' }}
								placement="top"
								classes={{ tooltip: classes.tooltip }}
								arrow>
								<span>{getSwitch(true)}</span>
							</Tooltip>
						) : (
							getSwitch(false)
						)}
						<Box style={{ flex: 1 }} />
						<Tooltip
							title={INCLUDE_DATA_HELP}
							classes={{ tooltip: classes.tooltip }}
							placement="top-end"
							arrow>
							<HelpOutlineIcon style={{ marginRight: '8px', fontSize: '20px' }} />
						</Tooltip>
					</Box>

					<Box className={classes.buttonContainer}>
						<Box>
							<Button
								id="remove-access-save-btn"
								disabled={shouldDisable}
								color="primary"
								className={`${classes.downloadButton} ${classes.drawerButton}`}
								onClick={grantAccess}>
								{SEND}
							</Button>
						</Box>
						<Box>
							<Button
								id="remove-access-cancel-btn"
								variant="outlined"
								color="primary"
								className={`${classes.drawerButton}`}
								onClick={toggleDrawer}>
								{CANCEL}
							</Button>
						</Box>
					</Box>
				</Drawer>
			</Box>
			<ShowShareReportDialog />
			<RBSnackbar
				doOpen={openSnackbar.open}
				msg={openSnackbar.msg}
				type={openSnackbar.type}
				doClose={() => {
					setSnackbar({ open: false, msg: '' });
				}}
			/>
		</>
	);
};

ShareReport.propTypes = {
	setShow: PropTypes.func.isRequired,
	show: PropTypes.bool.isRequired,
	handleBack: PropTypes.func,
	syncWithBE: PropTypes.bool
};

export default ShareReport;
